export const managementAgreementStatuses=[
  'Not started','Drafting','With prospective client','Agreed','Awaiting signatures','Executed',
] as const;

export type ManagementAgreementStatus=(typeof managementAgreementStatuses)[number];

export const managementAgreementTransitions:Record<ManagementAgreementStatus,readonly ManagementAgreementStatus[]>={
  'Not started':['Drafting'],
  Drafting:['With prospective client'],
  'With prospective client':['Drafting','Agreed'],
  Agreed:['Drafting','Awaiting signatures'],
  'Awaiting signatures':['Agreed'],
  Executed:[],
};

export type ManagementAgreementTerms={
  clientPartyType?:'Person';
  clientPersonId?:string;
  clientName:string|null;
  clientCompanyNumber:string|null;
  clientAddressType:'registered'|'correspondence'|null;
  clientAddress:string|null;
  clientRegisteredOffice:string|null;
  clientCorrespondenceAddress:string|null;
  clientEmail:string|null;
  clientAgentName:string|null;
  buildingName:string|null;
  buildingAddress:string|null;
  postcode:string|null;
  residentialUnits:number|null;
  commercialUnits:number|null;
  propertyDescription:string|null;
  agreementDate:string|null;
  proposedCommencementDate:string|null;
  mobilisationFee:string|null;
  mobilisationScopeReference:string|null;
  annualManagementFee:string|null;
  residentialManagementFee:string|null;
  commercialManagementFee:string|null;
  managementFeePaymentFrequency:string|null;
  periodicManagementFee:string|null;
  managementFeePeriod:string|null;
  commonPartsInspectionFrequency:string|null;
  propertyVisitFrequency:string|null;
  delegatedExpenditureLimit:string|null;
  liabilityCap:string|null;
  lpe1Fee:string|null;
  conveyancingFeeScheduleId?:string|null;
  conveyancingFeeScheduleText?:string|null;
  qualifyingWorksProjectManagementFeeBasis:string|null;
  majorWorksProjectManagementFeeBasis:string|null;
  companySecretarialStatus:'Included'|'Optional'|'Excluded'|'Unknown'|null;
  companySecretarialChargingBasis:string|null;
  companySecretarialFeeTerm:string|null;
  buildingOver11m:'Yes'|'No'|'Unknown'|null;
  higherRiskBuilding:'Yes'|'No'|'Unknown'|null;
  handoverScenario:'INCUMBENT_MANAGING_AGENT'|'NO_INCUMBENT'|'ACQUISITION_OR_INCOMPLETE_RECORDS'|'RTM_TRANSITION'|null;
  tpiCorporateMembershipPending:boolean|null;
  tpiMembershipDeadline:string|null;
  managementFeeAmount:string|null;
  managementFeeBasis:string|null;
  vatTreatment:string|null;
  initialTermMonths:number|null;
  noticePeriodMonths:number|null;
  scopeSummary:string|null;
  specialTerms:string|null;
};

export type ManagementAgreement={
  id:string;
  agreement_number:string;
  enquiry_id:string;
  development_id:string|null;
  status:ManagementAgreementStatus;
  current_version_id:string|null;
  client_review_version_id:string|null;
  client_review_started_at:string|null;
  agreed_version_id:string|null;
  agreed_at:string|null;
  agreed_by:string|null;
  signature_version_id:string|null;
  executed_document_id:string|null;
  executed_document_number:string|null;
  executed_document_title:string|null;
  executed_document_sensitivity:string|null;
  executed_version_id:string|null;
  executed_at:string|null;
  executed_by:string|null;
  created_by:string;
  updated_by:string;
  created_by_name:string;
  updated_by_name:string;
  created_at:string;
  updated_at:string;
  version:number;
};

export type ManagementAgreementVersion={
  id:string;
  management_agreement_id:string;
  version_number:number;
  terms_snapshot:ManagementAgreementTerms;
  management_fee_amount:string|null;
  draft_body:string|null;
  change_summary:string;
  template_key:string|null;
  template_revision:string|null;
  template_snapshot:ManagementAgreementTemplate|null;
  clause_changes_snapshot:ManagementAgreementClauseChange[];
  content_snapshot_sha256:string|null;
  created_by:string;
  created_at:string;
  created_by_name:string;
};

export type ManagementAgreementClause={
  clauseKey:string;heading:string|null;standardText:string;templateRevision:string;
  type:'SECTION'|'CLAUSE'|'APPENDIX';order:number;modifiable:boolean;
};

export type ManagementAgreementTemplate={
  metadata:{templateKey:string;templateRevision:string;displayName:string;sourceDocumentName:string;sourceSha256:string};
  cover:{title:string;lines:string[];contents:string[];signatureBlocks:string[]};
  clauses:ManagementAgreementClause[];
  appendices:Array<ManagementAgreementClause&{paragraphs:string[];tables:Array<{rows:string[][]}>}>;
};

export type ManagementAgreementClauseChange={
  clauseKey:string;standardText:string;action:'OVERRIDE'|'OMIT';effectiveText:string|null;
  reason:string;createdBy:string;createdAt:string;
};

export type ManagementAgreementRendering={
  id:string;management_agreement_id:string;agreement_version_id:string;template_key:string;
  template_revision:string;format:'PDF';purpose:'Preview'|'Signature';
  status:'Pending'|'Processing'|'Completed'|'Failed';document_id:string|null;
  document_version_id:string|null;document_number:string|null;document_title:string|null;
  checksum_sha256:string|null;renderer_revision:string;render_input_sha256:string;
  created_by:string;created_by_name:string;created_at:string;completed_at:string|null;
  attempts:number;last_error:string|null;
};

export type ESignRecipient={
  id:string;role:'CLIENT'|'MANAGER';name:string;email:string;title:string;signing_order:number;
  status:'Pending'|'Sent'|'Viewed'|'Signed'|'Declined'|'Bounced';
  viewed_at:string|null;signed_at:string|null;declined_at:string|null;
};

export type ManagementAgreementESignRequest={
  id:string;source_document_id:string;source_document_version_id:string;source_checksum_sha256:string;
  agreement_version_id:string;agreement_version_number:number;
  provider_code:string;provider_document_id:string|null;
  status:'Queued'|'Preparing'|'Ready'|'Sending'|'Sent'|'Viewed'|'Partially signed'|'Retrieving'|
    'Completed'|'Declined'|'Expired'|'Cancelled'|'Bounced'|'Failed';
  provider_status:string|null;test_mode:boolean;created_by:string;created_by_name:string;
  confirmed_at:string;created_at:string;sent_at:string|null;completed_at:string|null;terminal_at:string|null;
  final_signed_document_id:string|null;final_signed_document_version_id:string|null;
  final_signed_checksum_sha256:string|null;final_signed_received_at:string|null;
  final_document_number:string|null;final_document_title:string|null;last_error:string|null;
  can_retry:boolean;is_active:boolean;recipients:ESignRecipient[];
};

export type ManagementAgreementDeviationSummary={
  standardClauses:number;modifiedClauses:number;omittedClauses:number;
  deviations:Array<{clauseKey:string;action:'Modified'|'Omitted';standardText:string;
    currentText:string|null;reason:string;diff:Array<{type:'removed'|'added';text:string}>}>;
};

export type EligibleExecutedDocument={
  id:string;
  document_number:string;
  title:string;
  reference_number?:string|null;
};

export type EligibleManagementAppointment={
  id:string;
  version:number;
  client_name:string;
  managing_agent_name:string;
  starts_on:string;
  ends_on:string|null;
  status:string;
  management_agreement_document_id:string|null;
};

export type ManagementAgreementMobilisation={
  agreement:{
    id:string;agreementNumber:string;status:string;executedAt:string|null;executedDocumentId:string|null;
    executedDocumentVersionId:string|null;executedDocumentChecksumSha256:string|null;
  };
  development:{
    id:string;code:string;name:string;status:string;management_status:string;
    management_start_date:string|null;in_contract:boolean;
  }|null;
  appointment:{
    id:string;status:string;starts_on:string;ends_on:string|null;
    client_name:string;manager_name:string;
  }|null;
  programme:{
    id:string;onboarding_number:string;status:string;proposed_go_live_date:string|null;
    actual_go_live_at:string|null;onboarding_completed_at:string|null;
  }|null;
  requirements:Array<{
    id:string;requirement_code:string;title:string;status:string;data_state:string;
    blocking:boolean;completion_required:boolean;
  }>;
  actions:Array<{
    id:string;action_number:string;title:string;description:string|null;priority:string;
    status:string;due_at:string|null;source_event_type:string;
  }>;
  exceptions:Array<{
    id:string;exception_kind:string;severity:string;status:string;title:string;details:string;
    due_at:string|null;raised_at:string;
  }>;
  operational?:{status:string;detail:string;managementStart:string|null;managementEnd:string|null}|null;
  originCommunicationThreadId:string|null;
  readiness:{
    operationalBlockerCount:number;onboardingGapCount:number;
    operationalGoLiveRequiresHumanApproval:boolean;
  };
};

export type ManagementAgreementPrefill={
  terms:ManagementAgreementTerms;
  estimatedAnnualValueSuggestion:string|number|null;
};

export type ManagementAgreementWorkspace={
  commercialHandover?:unknown;
  agreement:ManagementAgreement|null;
  currentVersion:ManagementAgreementVersion|null;
  versions:ManagementAgreementVersion[];
  renderings:ManagementAgreementRendering[];
  eSignRequests:ManagementAgreementESignRequest[];
  eligibleExecutedDocuments:EligibleExecutedDocument[];
  eligibleAppointments:EligibleManagementAppointment[];
  canLinkManagementAppointment:boolean;
  mobilisation:ManagementAgreementMobilisation|null;
  canReconcileMobilisation:boolean;
  canGeneratePdf:boolean;
  canSendESign:boolean;
  eSignAvailability:{available:boolean;provider:string;testMode:boolean|null;message:string};
  approvedTemplateConfigured:boolean;
  template:ManagementAgreementTemplate['metadata'];
  deviationSummary:ManagementAgreementDeviationSummary|null;
  signatureReadiness:{ready:boolean;gaps:Array<{field:string;message:string}>}|null;
  prefill:ManagementAgreementPrefill;
};

const text=(form:FormData,name:string)=>{
  const value=String(form.get(name)??'').trim();
  return value||null;
};

const integer=(form:FormData,name:string)=>{
  const value=text(form,name);
  return value===null?null:Number(value);
};

const boolean=(form:FormData,name:string)=>{
  const value=text(form,name);
  return value===null?null:value==='true';
};

export function managementAgreementTermsFromForm(form:FormData):ManagementAgreementTerms{
  return{
    ...(form.get('clientPartyType')==='Person'?{clientPartyType:'Person' as const,clientPersonId:String(form.get('clientPersonId')??'')}:{}),
    clientName:text(form,'clientName'),
    clientCompanyNumber:text(form,'clientCompanyNumber'),
    clientAddressType:text(form,'clientAddressType') as ManagementAgreementTerms['clientAddressType'],
    clientAddress:text(form,'clientAddress'),
    clientRegisteredOffice:text(form,'clientRegisteredOffice'),
    clientCorrespondenceAddress:text(form,'clientCorrespondenceAddress'),
    clientEmail:text(form,'clientEmail'),
    clientAgentName:text(form,'clientAgentName'),
    buildingName:text(form,'buildingName'),
    buildingAddress:text(form,'buildingAddress'),
    postcode:text(form,'postcode'),
    residentialUnits:integer(form,'residentialUnits'),
    commercialUnits:integer(form,'commercialUnits'),
    propertyDescription:text(form,'propertyDescription'),
    agreementDate:text(form,'agreementDate'),
    proposedCommencementDate:text(form,'proposedCommencementDate'),
    mobilisationFee:text(form,'mobilisationFee'),
    mobilisationScopeReference:text(form,'mobilisationScopeReference'),
    annualManagementFee:text(form,'annualManagementFee'),
    residentialManagementFee:text(form,'residentialManagementFee'),
    commercialManagementFee:text(form,'commercialManagementFee'),
    managementFeePaymentFrequency:text(form,'managementFeePaymentFrequency'),
    periodicManagementFee:text(form,'periodicManagementFee'),
    managementFeePeriod:text(form,'managementFeePeriod'),
    commonPartsInspectionFrequency:text(form,'commonPartsInspectionFrequency'),
    propertyVisitFrequency:text(form,'propertyVisitFrequency'),
    delegatedExpenditureLimit:text(form,'delegatedExpenditureLimit'),
    liabilityCap:text(form,'liabilityCap'),
    lpe1Fee:text(form,'lpe1Fee'),
    conveyancingFeeScheduleId:text(form,'conveyancingFeeScheduleId'),
    conveyancingFeeScheduleText:text(form,'conveyancingFeeScheduleText'),
    qualifyingWorksProjectManagementFeeBasis:text(form,'qualifyingWorksProjectManagementFeeBasis'),
    majorWorksProjectManagementFeeBasis:text(form,'majorWorksProjectManagementFeeBasis'),
    companySecretarialStatus:text(form,'companySecretarialStatus') as ManagementAgreementTerms['companySecretarialStatus'],
    companySecretarialChargingBasis:text(form,'companySecretarialChargingBasis'),
    companySecretarialFeeTerm:text(form,'companySecretarialFeeTerm'),
    buildingOver11m:text(form,'buildingOver11m') as ManagementAgreementTerms['buildingOver11m'],
    higherRiskBuilding:text(form,'higherRiskBuilding') as ManagementAgreementTerms['higherRiskBuilding'],
    handoverScenario:text(form,'handoverScenario') as ManagementAgreementTerms['handoverScenario'],
    tpiCorporateMembershipPending:boolean(form,'tpiCorporateMembershipPending'),
    tpiMembershipDeadline:text(form,'tpiMembershipDeadline'),
    managementFeeAmount:text(form,'managementFeeAmount'),
    managementFeeBasis:text(form,'managementFeeBasis'),
    vatTreatment:text(form,'vatTreatment'),
    initialTermMonths:integer(form,'initialTermMonths'),
    noticePeriodMonths:integer(form,'noticePeriodMonths'),
    scopeSummary:text(form,'scopeSummary'),
    specialTerms:text(form,'specialTerms'),
  };
}

export function managementAgreementClauseChangesFromForm(
  form:FormData,template:ManagementAgreementTemplate|null,
):Array<{clauseKey:string;action:'OVERRIDE'|'OMIT';effectiveText:string|null;reason:string}>{
  if(!template)return[];
  return[...template.clauses,...template.appendices].flatMap(clause=>{
    if(!clause.modifiable)return[];
    const action=String(form.get(`clauseAction:${clause.clauseKey}`)??'STANDARD');
    if(action!=='OVERRIDE'&&action!=='OMIT')return[];
    return[{clauseKey:clause.clauseKey,action,effectiveText:action==='OVERRIDE'
      ?text(form,`clauseText:${clause.clauseKey}`):null,
    reason:text(form,`clauseReason:${clause.clauseKey}`)??''}];
  });
}

export function formatManagementAgreementMoney(value:string|number|null|undefined){
  if(value===null||value===undefined)return'Unknown';
  const canonical=String(value),match=/^(\d+)(?:\.(\d{1,2}))?$/.exec(canonical);
  if(!match)return`£${canonical}`;
  const[,whole,fraction='']=match;
  return`£${whole!.replace(/\B(?=(\d{3})+(?!\d))/g,',')}.${fraction.padEnd(2,'0')}`;
}
